import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import type * as RT from '../realtime/realtime-events.gen';
import {
  recordRealtimeConnect,
  recordRealtimeError,
  recordRealtimeEvent,
  recordRealtimeReconnect,
  resetRealtimeStatus,
  setRealtimeStatus,
} from '../realtime/status-store';
import { ensureFreshAccessToken, readSession, renewAccessToken } from './http-client';

export interface EventMap {
  BookingCreated: RT.BookingCreatedPayload;
  BookingCancelled: RT.BookingCancelledPayload;
  BookingRescheduled: RT.BookingRescheduledPayload;
  BookingConfirmed: RT.BookingConfirmedPayload;
  BookingExtensionChanged: RT.BookingExtensionChangedPayload;
  AccessEntry: RT.AccessEventPayload;
  AccessDenied: RT.AccessEventPayload;
  TaskCreated: RT.TaskPayload;
  TaskUpdated: RT.TaskPayload;
  TaskCompleted: RT.TaskPayload;
  OpenMatchChanged: RT.OpenMatchChangedPayload;
  ScheduleBlockChanged: RT.ScheduleBlockChangedPayload;
  CourtCatalogChanged: RT.CourtCatalogChangedPayload;
  TrainerScheduleChanged: RT.TrainerScheduleChangedPayload;
  GroupSessionChanged: RT.GroupSessionChangedPayload;
  TournamentCreated: RT.TournamentCreatedPayload;
  TournamentRescheduled: RT.TournamentRescheduledPayload;
  TournamentCancelled: RT.TournamentCancelledPayload;
  TournamentStatusChanged: RT.TournamentStatusChangedPayload;
  TournamentBracketGenerated: RT.TournamentBracketGeneratedPayload;
  TournamentRoundStarted: RT.TournamentRoundStartedPayload;
  TournamentRoundCompleted: RT.TournamentRoundCompletedPayload;
  TournamentCompleted: RT.TournamentCompletedPayload;
  TournamentPlayerRegistered: RT.TournamentPlayerRegisteredPayload;
  TournamentPlayerUnregistered: RT.TournamentPlayerUnregisteredPayload;
  MatchScoreUpdated: RT.MatchScoreUpdatedPayload;
  PosSaleConfirmed: RT.PosSaleConfirmedPayload;
  RefundCompleted: RT.RefundCompletedPayload;
  AssistanceCreated: RT.AssistanceCreatedPayload;
  AssistanceUpdated: RT.AssistanceUpdatedPayload;
  NotificationCreated: RT.NotificationCreatedPayload;
  ChatMessage: RT.ChatMessagePayload;
  ChatTyping: { conversationId: string; fromStaff: boolean; eventId?: string };
  ChatRead: { conversationId: string; uptoSeq: number; byStaff: boolean; eventId?: string };
  SlotViewed: RT.SlotPresencePayload;
  SlotReleased: RT.SlotPresencePayload;
}

type EventName = keyof EventMap;
type Listener = (payload: unknown) => void;

const listeners = new Map<EventName, Set<Listener>>();

const recentEventFingerprints = new Map<string, number>();
const DEDUP_WINDOW_MS = 60_000;
const fingerprintOf = (name: EventName, payload: unknown): string => {
  const eid = (payload as { eventId?: unknown } | null | undefined)?.eventId;
  if (typeof eid === 'string' && eid.length > 0) return `${name}::${eid}`;
  try {
    return `${name}::${JSON.stringify(payload)}`;
  } catch {
    return `${name}::__unstringifiable__`;
  }
};
const pruneFingerprints = (now: number): void => {
  for (const [k, ts] of recentEventFingerprints) {
    if (now - ts > DEDUP_WINDOW_MS) recentEventFingerprints.delete(k);
  }
};

const dispatch = (name: EventName, payload: unknown): void => {
  const now = performance.now();
  pruneFingerprints(now);
  const fp = fingerprintOf(name, payload);
  if (recentEventFingerprints.has(fp)) {
    recordRealtimeEvent(name);
    return;
  }
  recentEventFingerprints.set(fp, now);
  recordRealtimeEvent(name);
  listeners.get(name)?.forEach((l) => {
    try {
      l(payload);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[signalr] listener for ${name} threw`, err);
    }
  });
};

export const realtimeHub = {
  on<K extends EventName>(name: K, listener: (payload: EventMap[K]) => void): () => void {
    let bucket = listeners.get(name);
    if (!bucket) {
      bucket = new Set();
      listeners.set(name, bucket);
    }
    bucket.add(listener as Listener);
    return () => {
      listeners.get(name)?.delete(listener as Listener);
    };
  },

  emit<K extends EventName>(name: K, payload: EventMap[K]): void {
    dispatch(name, payload);
  },
};

export const invokeHub = (method: string, ...args: unknown[]): void => {
  if (connection && connection.state === HubConnectionState.Connected) {
    void connection.invoke(method, ...args).catch(() => {
    });
  }
};

export const invokeHubFor = <T>(method: string, ...args: unknown[]): Promise<T> =>
  connection && connection.state === HubConnectionState.Connected
    ? connection.invoke<T>(method, ...args)
    : Promise.reject(new Error(`[signalr] ${method}: соединение не подключено`));

const connectedListeners = new Set<() => void>();
export const onRealtimeConnected = (listener: () => void): (() => void) => {
  connectedListeners.add(listener);
  return () => {
    connectedListeners.delete(listener);
  };
};
const notifyConnected = (): void => {
  for (const listener of connectedListeners) {
    try {
      listener();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[signalr] connected listener threw', err);
    }
  }
};

const HUB_URL = '/hubs/bookings';
export const EVENT_NAMES: readonly EventName[] = [
  'BookingCreated',
  'BookingCancelled',
  'BookingRescheduled',
  'BookingConfirmed',
  'BookingExtensionChanged',
  'ScheduleBlockChanged',
  'CourtCatalogChanged',
  'TrainerScheduleChanged',
  'GroupSessionChanged',
  'TournamentCreated',
  'TournamentRescheduled',
  'TournamentCancelled',
  'TournamentStatusChanged',
  'TournamentBracketGenerated',
  'TournamentRoundStarted',
  'TournamentRoundCompleted',
  'TournamentCompleted',
  'TournamentPlayerRegistered',
  'TournamentPlayerUnregistered',
  'MatchScoreUpdated',
  'TaskCreated',
  'TaskUpdated',
  'TaskCompleted',
  'AccessEntry',
  'AccessDenied',
  'PosSaleConfirmed',
  'RefundCompleted',
  'OpenMatchChanged',
  'AssistanceCreated',
  'AssistanceUpdated',
  'NotificationCreated',
  'ChatMessage',
  'ChatTyping',
  'ChatRead',
  'SlotViewed',
  'SlotReleased',
];

let connection: HubConnection | null = null;

let initialConnectRetryTimer: ReturnType<typeof setTimeout> | null = null;

export const startRealtime = async (): Promise<void> => {
  if (connection) return;
  if (!readSession()) return;

  setRealtimeStatus('connecting');
  let renewBeforeReconnect = false;
  const c = new HubConnectionBuilder()
    .withUrl(HUB_URL, {
      accessTokenFactory: async () => {
        if (renewBeforeReconnect) {
          renewBeforeReconnect = false;
          return (await renewAccessToken()) ?? '';
        }
        return (await ensureFreshAccessToken()) ?? '';
      },
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (ctx) => {
        const delays = [0, 1000, 2000, 5000, 10_000, 30_000];
        return delays[Math.min(ctx.previousRetryCount, delays.length - 1)] ?? null;
      },
    })
    .configureLogging(LogLevel.Warning)
    .build();
  connection = c;
  const current = (): boolean => connection === c;

  c.onreconnecting((err) => {
    if (!current()) return;
    renewBeforeReconnect = true;
    recordRealtimeError(err?.message ?? null);
    recordRealtimeReconnect();
    // eslint-disable-next-line no-console
    console.warn('[signalr] reconnecting:', err?.message ?? 'no message');
  });
  c.onreconnected((id) => {
    if (!current()) return;
    recordRealtimeConnect(id ?? null);
    notifyConnected();
    // eslint-disable-next-line no-console
    console.log('[signalr] reconnected, connectionId=', id);
  });
  c.onclose((err) => {
    if (!current()) return;
    recordRealtimeError(err?.message ?? null);
    setRealtimeStatus(err ? 'failed' : 'disconnected');
    // eslint-disable-next-line no-console
    console.warn('[signalr] closed:', err?.message ?? 'graceful');
  });

  for (const name of EVENT_NAMES) {
    c.on(name, (payload: unknown) => {
      if (current()) dispatch(name, payload);
    });
  }

  try {
    await c.start();
    if (!current()) return;
    recordRealtimeConnect(c.connectionId ?? null);
    if (initialConnectRetryTimer) {
      clearTimeout(initialConnectRetryTimer);
      initialConnectRetryTimer = null;
    }
    notifyConnected();
  } catch (err) {
    if (!current()) return;
    // eslint-disable-next-line no-console
    console.warn('[signalr] initial connect failed; will retry in 5s', err);
    recordRealtimeError(err instanceof Error ? err.message : String(err));
    setRealtimeStatus('failed');
    connection = null;
    initialConnectRetryTimer = setTimeout(() => {
      initialConnectRetryTimer = null;
      void startRealtime();
    }, 5000);
  }
};

export const stopRealtime = async (): Promise<void> => {
  if (initialConnectRetryTimer) {
    clearTimeout(initialConnectRetryTimer);
    initialConnectRetryTimer = null;
  }
  const c = connection;
  connection = null;
  resetRealtimeStatus();
  if (!c || c.state === HubConnectionState.Disconnected) return;
  try {
    await c.stop();
  } catch {
  }
};

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    void stopRealtime();
  });
  setTimeout(() => {
    void startRealtime();
  }, 150);
}
